import { find } from 'es-toolkit/compat';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import React, { useCallback, useRef } from 'react';
import { StringParam, UrlUpdateType, useQueryParam } from 'use-query-params';

import { SnackbarCloseButton } from 'components/Snackbars/SnackbarCloseButton';
import type { Lab } from 'api/labs';
import { Permission } from 'interfaces/permissionOption';
import { useAppSelector } from 'redux/hooks';
import { useLabs } from 'utils/queryHooks/useLabs';
import { usePermissions } from 'utils/usePermissions';

const LAB_SWITCH_SNACKBAR_KEY = 'lab-switch';

/**
 * hook for doing operations with a labId
 * @returns the current lab id from the query param or the default lab id from the store
 */
export const useCurrentLabId = (): {
  userLabId: string; // the default labId from the store
  labId: string; // the current labId from the query param or the default labId from the store
  currentLab?: Lab;
  queryLabId?: string; // the current labId from the query param;
  setLabId: (newLabId: string, updateType?: UrlUpdateType) => void; // set the labId to a new one in the query param
  stringifyWithLab: (search: Object) => string; // stringify a search object and add a labId to it, without the ?
  labSearch: string; // if already has labId in query: ?labId={labId} else ''
} => {
  const [labId, setLabIdState] = useQueryParam('labId', StringParam, { updateType: 'replaceIn' });
  const { defaultLabId: userLabId } = useAppSelector((state) => state.auth);
  const effectiveLabId = labId || userLabId;

  const { hasPermission } = usePermissions();
  const canViewMultipleLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);

  const { data: labs, isLoading } = useLabs();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const lastNotifiedLabIdRef = useRef<string | null>(null);

  const setLabId: typeof setLabIdState = useCallback(
    (newLabId: string, updateType: UrlUpdateType = 'replaceIn') => {
      if (newLabId === effectiveLabId) {
        return;
      }

      if (!canViewMultipleLabs) {
        return;
      }

      if (isLoading) {
        setLabIdState(newLabId, updateType);
        return;
      }

      const labDetails = find(labs, { id: newLabId });
      if (labDetails) {
        setLabIdState(newLabId, updateType);
        const labName = labDetails?.name || newLabId;

        if (lastNotifiedLabIdRef.current === newLabId) {
          return;
        }

        lastNotifiedLabIdRef.current = newLabId;
        closeSnackbar(LAB_SWITCH_SNACKBAR_KEY);
        enqueueSnackbar(`Switched to lab: ${labName}`, {
          key: LAB_SWITCH_SNACKBAR_KEY,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        });
      } else {
        enqueueSnackbar(`Lab with ID ${newLabId} not found or you do not have access to it.`, {
          variant: 'error',
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        });
      }
    },
    [canViewMultipleLabs, closeSnackbar, effectiveLabId, enqueueSnackbar, isLoading, labs, setLabIdState]
  );

  const stringifyWithLab = (search: Object) => stringify({ ...search, labId: effectiveLabId });
  const labSearch = labId ? `?${stringify({ labId })}` : '';

  return {
    labId: effectiveLabId,
    currentLab: find(labs, { id: effectiveLabId }),
    setLabId,
    stringifyWithLab,
    labSearch,
    userLabId,
    queryLabId: labId,
  };
};
